import * as XLSX from 'xlsx'
import Papa from 'papaparse'
import { ValidationError } from '@/types/onboarding'

export interface ParseResult {
  data: any[]
  headers: string[]
  errors: ValidationError[]
}

export const parseExcelFile = async (file: File, preferredSheetName?: string): Promise<ParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = e.target?.result
        if (!data) {
          reject(new Error('No se pudo leer el archivo'))
          return
        }

        const workbook = XLSX.read(data, { type: 'array' })
        const matchingSheet = preferredSheetName
          ? workbook.SheetNames.find((name) => name.trim().toLocaleLowerCase() === preferredSheetName.trim().toLocaleLowerCase())
          : null
        const worksheet = workbook.Sheets[matchingSheet ?? workbook.SheetNames[0]]

        if (!worksheet) {
          reject(new Error('El archivo Excel está vacío'))
          return
        }

        const rows: any[] = XLSX.utils.sheet_to_json(worksheet)

        if (rows.length === 0) {
          reject(new Error('El archivo no contiene datos'))
          return
        }

        const headers = Object.keys(rows[0] as Record<string, unknown>)

        resolve({
          data: rows,
          headers,
          errors: [],
        })
      } catch (err) {
        reject(err)
      }
    }

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'))
    }

    reader.readAsArrayBuffer(file)
  })
}

export const parseCSVFile = async (file: File): Promise<ParseResult> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        if (results.data.length === 0) {
          reject(new Error('El archivo CSV está vacío'))
          return
        }

        const headers = Object.keys(results.data[0])

        resolve({
          data: results.data,
          headers,
          errors: results.errors.map((err: any, idx: number) => ({
            row: idx + 2,
            message: err.message,
          })),
        })
      },
      error: (error: any) => {
        reject(error)
      },
    })
  })
}

export const validateEmpresaData = (
  data: any[],
  _existingEmails?: Set<string>
): ValidationError[] => {
  const errors: ValidationError[] = []

  for (let i = 0; i < data.length; i++) {
    const row = data[i]

    // Validar nombre
    if (!row.nombre || !String(row.nombre).trim()) {
      errors.push({
        row: i + 2,
        message: 'Campo "nombre" es requerido',
        field: 'nombre',
      })
    }

    // Validar estado_empresa si existe
    if (row.estado_empresa && !['activo', 'inactivo', 'prospecto'].includes(String(row.estado_empresa).toLowerCase())) {
      errors.push({
        row: i + 2,
        message: `Estado "${row.estado_empresa}" no es válido (use: activo, inactivo, prospecto)`,
        field: 'estado_empresa',
      })
    }
  }

  return errors
}

export const validateContactoData = (
  data: any[],
  existingEmails?: Set<string>
): ValidationError[] => {
  const errors: ValidationError[] = []

  for (let i = 0; i < data.length; i++) {
    const row = data[i]

    // Validar nombre
    if (!row.nombre || !String(row.nombre).trim()) {
      errors.push({
        row: i + 2,
        message: 'Campo "nombre" es requerido',
        field: 'nombre',
      })
    }

    // Validar email
    if (!row.email || !String(row.email).trim()) {
      errors.push({
        row: i + 2,
        message: 'Campo "email" es requerido',
        field: 'email',
      })
    } else {
      const email = String(row.email).trim().toLowerCase()
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!emailRegex.test(email)) {
        errors.push({
          row: i + 2,
          message: `Email inválido: "${email}"`,
          field: 'email',
        })
      }

      // Detectar duplicados con datos existentes
      if (existingEmails?.has(email)) {
        errors.push({
          row: i + 2,
          message: `Email ya existe en la BD: "${email}"`,
          field: 'email',
        })
      }
    }
  }

  return errors
}

export const detectDuplicates = (data: any[], field: string): Map<string, number[]> => {
  const duplicates = new Map<string, number[]>()

  for (let i = 0; i < data.length; i++) {
    const value = String(data[i][field]).trim().toLowerCase()
    if (!duplicates.has(value)) {
      duplicates.set(value, [])
    }
    duplicates.get(value)!.push(i + 2)
  }

  // Filtrar solo los que aparecen más de una vez
  const result = new Map<string, number[]>()
  for (const [key, rows] of duplicates) {
    if (rows.length > 1) {
      result.set(key, rows)
    }
  }

  return result
}
